import React, { useState, useEffect } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import dayjs from 'dayjs';
import _ from 'lodash';

import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useUsers from '../../context/UsersContext';
import useMenus from '../../context/MenuContext';
import masterDataService from '../../service/masterData.service';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI, ButtonUI, FormBase } from '../Interface';
import { generateAutocompleteItems } from '../../utils/helper';
import useParam from '../../context/ParamContext';
import employeeAttendenceService from '../../service/employeeAttendence.service';
import EmployeeAttendanceFields from './EmployeeAttendanceFields';
import EmployeeAttendanceReports from './EmployeeAttendanceReports';
import EmployeeAttendanceTabs from './EmployeeAttendanceTabs';
import useConfig from '../../context/ConfigContext';

export const EmployeeAttendancePage = () => {
  const { getUser, user } = useUsers();
  const { config, getConfig } = useConfig();
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const { getParam, params } = useParam();
  const [initValues, setInitValues] = useState({});
  const [refetchTable, setRefetchTable] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewField, setViewField] = useState(true);
  const [editButtonVisible, setEditButtonvisible] = useState(true);
  const [addField, setAddField] = useState(true);
  const [selectedValues, setSelectedValues] = useState({});
  const [operatorsDisabled, setOperatorsDisabled] = useState(false);
  const [submenu, setSubmenu] = useState(null);
  const [autocompleteData, setAutocompleteData] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [canEdit, setCanEdit] = useState(true);
  const [selectedRow, setSelectedRow] = useState({
    id: [],
  });
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    status: [],
    project: [],
    employees: [],
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const defaultEmployeeLine = {
    id: null,
    isNew: true,
    employeeId: null,
    rating: null,
    fromTime: null,
    toTime: null,
    timeSpent: null,
    createdBy: user.userId,
  };

  const def = {
    date: dayjs(new Date()),
    projectId: null,
    statusId: null,
    employeeAttendanceLine: [],
  };

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(config).length) getConfig();
    getMenus();
    getParam();

    setDefaultValues(def);
  }, []);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          statusId: autocompleteData?.status?.defaultValue ?? null,
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          status: autocompleteData?.status?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    const submenuId = getMenuSubMenuId('employeeAttendance');
    setSubmenu(submenuId);
    setCanEdit(getCanEdit(submenuId));
  }, [menus]);

  useEffect(() => {
    if (submenu) initDefaultValues();
  }, [submenu]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  const updateBatchedStatuses = (values) => {
    const updData = {
      statusId: values.statusId,
      selectedRows: selectedRow.id,
    };
    employeeAttendenceService.updateBatchedStatus(updData).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setSelectedRow({
        id: [],
      });
    });
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .employeesToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          status: resp[0],
          project: resp[1],
          employees: resp[2],
        };
        setAutocompleteData(aData);

        const acOptions = {
          status: generateAutocompleteItems(aData.status.statusItems, 'itemName', 'itemId'),
          project: generateAutocompleteItems(aData.project, 'projectName', 'projectId'),
          employees: generateAutocompleteItems(aData.employees, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const valuesToForm = () => {
    const { date, projectId, statusId, orderNumber, employeeAttendanceLine } = selectedValues;

    setInitValues({
      date: dayjs(date),
      projectId,
      statusId,
      orderNumber,
      employeeAttendanceLine: employeeAttendanceLine.map((au) => ({
        id: au.id,
        employeeId: au.employeeId,
        rating: au.rating,
        fromTime: au.fromTime,
        toTime: au.toTime,
        attendanceId: au.id,
        timeSpent: au.timeSpent,
        createdBy: au.createdBy,
      })),
    });
  };

  const validationSchema = Yup.object().shape({
    date: Yup.string().required(),
    projectId: Yup.string().required(),
    statusId: Yup.string().required(),
    orderNumber: Yup.string().nullable(),
    employeeAttendanceLine: Yup.array().of(
      Yup.object().shape({
        employeeId: Yup.string().required(),
        timeSpent: Yup.number().required().positive().max(24),
        rating: Yup.number().nullable().min(0).max(9.99),
        fromTime: Yup.string().required(),
        toTime: Yup.string().required(),
      })
    ),
  });

  const insertObject = (values) => {
    const insertObject = {
      ...values,
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      userId: user.userId,
      createdBy: user.userId,
    };

    employeeAttendenceService
      .createRow(insertObject)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        setRefetchTable(true);
        setAddField(true);
        setViewField(true);
        setSelectedValues(data);
        setRowSelectionModel(data.id);
      })

      .finally(() => {
        //@TODO loader
      });
  };

  const updateObject = (values) => {
    const updData = {
      ...values,
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      updatedBy: user.userId,
    };

    employeeAttendenceService.updateRow(updData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField) {
      insertObject(values, resetForm);
    }
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    employeeAttendenceService.deleteRow(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);
    });
  };

  const handleTemplate = () => {
    employeeAttendenceService.getExcelTemplate().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleImport = (files) => {
    employeeAttendenceService.importExcel(files, user.userId).then(() => {
      setRefetchTable(true);
      addNotification({
        content: 'Az import sikeres!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  return (
    <>
      <FormBase
        name="EmployeeAttendance"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                addClick={() => {
                  setSelectedValues(defaultSelectedValues);
                  setInitValues(defaultValues);
                  setViewField(false);
                  setAddField(false);
                }}
                addDisabled={!viewField || !canEdit}
                modClick={() => {
                  setViewField(false);
                  setEditButtonvisible(false);
                }}
                modDisabled={!viewField || operatorsDisabled || !canEdit}
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!viewField || operatorsDisabled || !canEdit}
                copyClick={() => {
                  setViewField(false);
                  setAddField(false);
                }}
                copyDisabled={!viewField || operatorsDisabled || !canEdit}
              />

              <Formik
                initialValues={initValues}
                validationSchema={validationSchema}
                validateOnChange={false}
                onSubmit={handleSubmit}
                enableReinitialize
              >
                {({ values, resetForm }) => (
                  <Form>
                    <EmployeeAttendanceFields
                      viewField={viewField}
                      selectedValues={selectedValues}
                      selectedRow={selectedRow}
                      autocompleteOptions={autocompleteOptions}
                    />
                    <EmployeeAttendanceTabs
                      setRefetchTable={setRefetchTable}
                      selectedValues={selectedValues}
                      user={user}
                      disabled={viewField}
                      params={params}
                      defaultField={defaultEmployeeLine}
                      autocompleteOptions={autocompleteOptions}
                      handleTemplate={handleTemplate}
                      handleImport={handleImport}
                    />
                    {selectedRow.id.length === 0 && (
                      <FormButtonPanelUI
                        onCancelClick={() => {
                          setViewField(true);
                          setEditButtonvisible(true);
                          setAddField(true);
                          resetForm();
                        }}
                        disabled={editButtonVisible && addField}
                      />
                    )}
                    {selectedRow.id.length > 0 && (
                      <div className="flex justify-center">
                        <ButtonUI
                          className="bg-success"
                          text="rögzítés"
                          onClick={() => {
                            updateBatchedStatuses(values);
                          }}
                        />
                      </div>
                    )}
                  </Form>
                )}
              </Formik>
            </div>
          </>
        }
        rightPanelContent={
          <>
            <EmployeeAttendanceReports
              refetch={{ refetchTable, setRefetchTable }}
              values={{ selectedValues, setSelectedValues }}
              viewField={viewField}
              operatorsDisabled={operatorsDisabled}
              selectedRows={{ selectedRow, setSelectedRow }}
              defaultValues={defaultValues}
              editButtonVisible={editButtonVisible}
              autocompleteOptions={autocompleteOptions}
              rowSelection={{ rowSelectionModel, setRowSelectionModel }}
            />
          </>
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
